<script setup lang="ts">
/**
 * Courbe de montée en niveau — composant PARTAGÉ (style bâtiment, unifié).
 *
 * Affiche l'évolution d'une valeur (bonus, multiplicateur…) sur une fenêtre de
 * niveaux : axe + polyligne + points, et un croisillon vert pointillé sur le
 * niveau « mis en avant » (typiquement le prochain niveau). Utilisé par les
 * tooltips bâtiment ET recherche pour éviter deux graphiques différents qui
 * font la même chose. La taille est paramétrable (tooltip vs modale).
 *
 * Le parent fournit les points déjà calculés `{ level, value }` (la normalisation
 * min/max est faite ici), la légende et le niveau à mettre en avant.
 */
import { computed } from 'vue';

interface CurvePoint {
  level: number;
  value: number;
}

const props = withDefaults(
  defineProps<{
    /** Valeurs par niveau (déjà calculées). */
    points: CurvePoint[];
    /** Niveau mis en avant (croisillon vert) — ex. prochain niveau. Null = aucun. */
    highlightLevel?: number | null;
    /** Légende affichée au-dessus du graphe. */
    caption?: string;
    width?: number;
    height?: number;
    /** Affiche la valeur au-dessus de chaque point (mode modale). */
    showPointLabels?: boolean;
    /** Formatage du label d'un point (défaut ×x.xx). */
    pointLabelFormat?: (value: number) => string;
  }>(),
  {
    highlightLevel: null,
    caption: '',
    width: 220,
    height: 60,
    showPointLabels: false,
    pointLabelFormat: (v: number) => `×${v.toFixed(2)}`,
  },
);

// Points {x, y} : x réparti uniformément, y normalisé sur le min/max VISIBLE
// (préserve la lisibilité quand la fenêtre est haute). Marges identiques à la
// courbe bâtiment d'origine (4px bord, 6px haut, 4px bas) -> rendu inchangé ;
// la marge haute s'élargit quand on affiche les labels de points.
const xy = computed<{ x: number; y: number; level: number; value: number }[]>(() => {
  const pts = props.points;
  if (pts.length === 0) return [];
  const w = props.width;
  const h = props.height;
  const padX = 4;
  const padBottom = 4;
  const padTop = props.showPointLabels ? 16 : 6;
  const vals = pts.map(p => p.value);
  const min = Math.min(...vals);
  const max = Math.max(...vals, min + 0.01);
  const n = pts.length;
  return pts.map((p, i) => ({
    level: p.level,
    value: p.value,
    x: (n === 1 ? 0 : i / (n - 1)) * (w - 2 * padX) + padX,
    y: (h - padBottom) - ((p.value - min) / (max - min || 1)) * (h - padTop - padBottom),
  }));
});

const polyline = computed(() => xy.value.map(p => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' '));

const highlight = computed(() =>
  props.highlightLevel == null ? null : (xy.value.find(p => p.level === props.highlightLevel) ?? null),
);
</script>

<template>
  <div class="luc">
    <span v-if="caption" class="luc-caption text-muted">{{ caption }}</span>
    <svg
      :viewBox="`0 0 ${width} ${height}`"
      class="luc-svg"
      :style="{ height: `${height}px` }"
      preserveAspectRatio="none"
    >
      <line :x1="4" :y1="height - 4" :x2="width - 4" :y2="height - 4" class="luc-axis" />
      <polyline :points="polyline" class="luc-line" />
      <g v-for="p in xy" :key="`pt-${p.level}`">
        <circle :cx="p.x" :cy="p.y" r="2.5" class="luc-point" />
        <text v-if="showPointLabels" :x="p.x" :y="p.y - 8" text-anchor="middle" class="luc-point-label">
          {{ pointLabelFormat(p.value) }}
        </text>
      </g>
      <!-- Croisillon vert pointillé sur le niveau mis en avant (prochain upgrade). -->
      <template v-if="highlight">
        <line :x1="highlight.x" y1="4" :x2="highlight.x" :y2="height - 4" class="luc-next-v" />
        <line x1="4" :y1="highlight.y" :x2="width - 4" :y2="highlight.y" class="luc-next-h" />
        <circle :cx="highlight.x" :cy="highlight.y" r="4" class="luc-next-pt" />
        <text :x="highlight.x" :y="highlight.y - 8" text-anchor="middle" class="luc-next-label">
          N{{ highlight.level }}
        </text>
      </template>
    </svg>
  </div>
</template>

<style lang="scss" scoped>
.luc {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.luc-caption {
  font-size: $font-size-xs;
  font-family: $font-mono;
}
.luc-svg {
  display: block;
  width: 100%;
}
.luc-axis {
  stroke: $border-default;
  stroke-width: 1;
}
.luc-line {
  fill: none;
  stroke: $accent-primary;
  stroke-width: 2;
}
.luc-point {
  fill: $color-neon-cyan;
}
.luc-point-label {
  fill: $text-secondary;
  font-size: 9px;
  font-family: $font-mono;
}
.luc-next-v,
.luc-next-h {
  stroke: $color-success;
  stroke-width: 1;
  stroke-dasharray: 3 3;
  opacity: 0.85;
}
.luc-next-pt {
  fill: $color-success;
  stroke: $bg-primary;
  stroke-width: 1.5;
}
.luc-next-label {
  fill: $color-success;
  font-size: 9px;
  font-family: $font-mono;
}
</style>
